import styles from "./ServiceTypeFormFields.module.scss";
import { nameof } from "ts-simple-nameof";
import { IServiceTypeInputModel } from "../models/serviceTypeInputModel";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { AppColorPicker } from "../../../../common/components/forms/inputs/colorPicker/AppColorPicker";
import { TextEditor } from "../../../../common/components/forms/inputs/textEditor/TextEditor";
import { DefaultFormMultiSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { IServiceTypeAdditionalDataViewModel } from "../models/serviceTypeAdditionalDataViewModel";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useRef } from "react";
import { CurrencyType } from "../../../../common/enums";
import { DefaultToggle } from "../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { usePreReservationFormOptions } from "../../../../common/preReservationForms/usePreReservationFormOptions";
import { EmployeePortfolioField } from "../../../../common/components/forms/inputs/employeePortfolio/EmployeePortfolioField";
import { useAppContext } from "../../../../AppContext";
import { studioHidesServicePortfolios } from "../../../../common/infrastructure/helpers/studioHelper";

interface IServiceTypeFormFieldsProps {
  availableEmployees?: ISelectFieldModel[];
  additionalData?: IServiceTypeAdditionalDataViewModel;
}

export const ServiceTypeFormFields = ({
  availableEmployees,
  additionalData,
}: IServiceTypeFormFieldsProps) => {
  const { setValue, getValues } = useFormContext<IServiceTypeInputModel>();
  const { selectedStudio } = useAppContext();
  const hasDetectedInitialRange = useRef(false);
  const isPriceRange = useWatch<IServiceTypeInputModel, "isPriceRange">({
    name: "isPriceRange",
  });
  const minPrice = useWatch<IServiceTypeInputModel, "minPrice">({
    name: "minPrice",
  });
  const serviceTypeId = useWatch<IServiceTypeInputModel, "id">({ name: "id" });
  const selectedEmployeeIds =
    useWatch<IServiceTypeInputModel, "employeeIds">({ name: "employeeIds" }) ??
    [];
  const selectedEmployees = selectedEmployeeIds
    .map((id) => availableEmployees?.find((e) => e.id === id))
    .filter((e): e is ISelectFieldModel => !!e);
  const { formOptions, usesPreReservationForms } =
    usePreReservationFormOptions();

  // When loading existing data, detect if it's a price range (only once on initial load)
  useEffect(() => {
    if (hasDetectedInitialRange.current) return;

    const values = getValues();
    if (
      values.id &&
      values.minPrice !== undefined &&
      values.maxPrice !== undefined
    ) {
      const isRange = values.minPrice !== values.maxPrice;
      setValue("isPriceRange", isRange, { shouldDirty: false });
      hasDetectedInitialRange.current = true;
    }
  }, [getValues, setValue]);

  // When isPriceRange is false, automatically set maxPrice = minPrice
  useEffect(() => {
    if (!isPriceRange && minPrice !== undefined) {
      setValue("maxPrice", minPrice);
    }
  }, [isPriceRange, minPrice, setValue]);

  return (
    <div className={styles.root}>
      <DefaultInputField
        name={nameof<IServiceTypeInputModel>((x) => x.name)}
        label="Име на услугата"
        placeholder="Подстригване"
        required
      />

      <DefaultInputField
        name={nameof<IServiceTypeInputModel>((x) => x.durationInMinutes)}
        label="Продължителност (минути)"
        placeholder="60"
        type="number"
        min={5}
        max={480}
        required
      />

      <DefaultInputField
        name={nameof<IServiceTypeInputModel>((x) => x.bufferAfterMinutes)}
        label="Буфер след резервацията (минути)"
        placeholder="0"
        type="number"
        min={0}
        max={120}
        tooltip="Време след всяка резервация, в което служителят остава зает (почистване, подготовка). Клиентът вижда само продължителността на услугата. Буферът не продължава след края на работното време, затова последният час за деня остава свободен за записване."
      />

      <AppColorPicker
        name={nameof<IServiceTypeInputModel>((x) => x.color)}
        label="Цвят в календара"
      />

      <DefaultToggle
        name={nameof<IServiceTypeInputModel>((x) => x.allowOnlineBooking)}
        label="Позволено онлайн записване"
        tooltip="Когато е активирано, клиентите могат да се записват онлайн за тази услуга"
      />

      <DefaultToggle
        name={nameof<IServiceTypeInputModel>((x) => x.isRemote)}
        label="Дистанционна среща (Google Meet)"
        tooltip="Линкът се създава от Google акаунта на собственика (Моят профил)."
      />

      <DefaultToggle
        name={nameof<IServiceTypeInputModel>((x) => x.isPriceRange)}
        label="Цените са диапазон (от - до)"
        tooltip="Когато е активирано, можете да зададете минимална и максимална цена"
      />

      <RenderWhen condition={!isPriceRange}>
        <DefaultInputField
          name={nameof<IServiceTypeInputModel>((x) => x.minPrice)}
          placeholder="20"
          label="Цена"
          type="number"
          currency={CurrencyType.EUR}
          required
        />
      </RenderWhen>

      <RenderWhen condition={!!isPriceRange}>
        <div className={styles.priceRange}>
          <DefaultInputField
            name={nameof<IServiceTypeInputModel>((x) => x.minPrice)}
            placeholder="20"
            label="Минимална цена"
            type="number"
            width="100%"
            currency={CurrencyType.EUR}
            required
          />
          <DefaultInputField
            name={nameof<IServiceTypeInputModel>((x) => x.maxPrice)}
            placeholder="50"
            label="Максимална цена"
            type="number"
            width="100%"
            currency={CurrencyType.EUR}
            required
          />
        </div>
      </RenderWhen>

      <DefaultFormMultiSelectField
        label="Служители които извършват услугата"
        options={availableEmployees ?? []}
        name={nameof<IServiceTypeInputModel>((x) => x.employeeIds)}
        tooltip="Изберете служителите, които могат да извършват тази услуга"
      />

      <RenderWhen condition={!studioHidesServicePortfolios(selectedStudio)}>
        <EmployeePortfolioField
          serviceTypeId={serviceTypeId}
          employees={selectedEmployees}
        />
      </RenderWhen>

      <RenderWhen condition={!!additionalData?.studioSettings.usesHalls}>
        <DefaultFormSelectField
          name={nameof<IServiceTypeInputModel>((x) => x.hallId)}
          options={additionalData?.halls}
          label="Зала"
          emptyOptionLabel="Няма избрана зала"
          includeEmptyOption
        />
      </RenderWhen>

      <DefaultFormSelectField
        name={nameof<IServiceTypeInputModel>((x) => x.categoryId)}
        options={
          additionalData?.categories
            ?.filter((c) => !c.isDeleted)
            .map((c) => ({ id: c.id, name: c.name })) ?? []
        }
        label="Категория"
        emptyOptionLabel="Без категория"
        includeEmptyOption
        includeId={false}
        tooltip="Изберете категория за тази услуга. Категориите помагат за по-добра организация на услугите."
      />

      <RenderWhen condition={usesPreReservationForms}>
        <DefaultFormSelectField
          name={nameof<IServiceTypeInputModel>((x) => x.preReservationFormId)}
          options={formOptions}
          label="Формуляр преди резервация"
          emptyOptionLabel="Без формуляр"
          includeEmptyOption
          includeId={false}
          tooltip="Ако изберете формуляр, клиентите трябва да го попълнят и да се съгласят с него, преди да запишат час за тази услуга."
        />
      </RenderWhen>

      <TextEditor
        name={nameof<IServiceTypeInputModel>((x) => x.description)}
        label="Описание"
        placeholder="Професионално подстригване с измиване и стайлинг"
      />
    </div>
  );
};
